import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  ActiveRunState,
  MediaSyncResult,
  Playlist,
  PlaylistItem,
  PlaylistItemInsert,
  PlaylistRun,
  PlaylistTrigger,
  PlaylistTriggerInsert,
  PlaylistUpdate,
  PlaylistWithItems,
  Playout,
  PlayoutAsset,
  PlayoutAssetInsert,
  PlayoutConfig,
  PlayoutConfigUpdate,
  PlayoutCounters,
  PlayoutInsert,
  PlayoutReadiness,
  PlayoutUpdate,
  PlayoutWithAsset,
  RunWithLog,
} from "@production-hub/shared";

type Url = string | undefined | null;

const KEY = "bridge-playout";

async function request<T>(
  bridgeUrl: Url,
  path: string,
  init?: RequestInit,
): Promise<T> {
  if (!bridgeUrl) throw new Error("No bridge URL");
  const response = await fetch(`${bridgeUrl}/api/playout${path}`, {
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(
      typeof body?.error === "string"
        ? body.error
        : `Request failed: ${response.status}`,
    );
  }
  return response.status === 204 ? (undefined as T) : response.json();
}

function useInvalidate(bridgeUrl: Url) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [KEY, bridgeUrl] });
}

// ============ Config ============

export interface PlayoutConfigResponse {
  config: PlayoutConfig;
  /** null when no connection is selected; true when the ATEM is mocked */
  atem_offline: boolean | null;
  video_mode: { width: number; height: number } | null;
}

export const usePlayoutConfig = (bridgeUrl: Url) =>
  useQuery({
    queryKey: [KEY, bridgeUrl, "config"],
    queryFn: () => request<PlayoutConfigResponse>(bridgeUrl, "/config"),
    enabled: !!bridgeUrl,
  });

export const useUpdatePlayoutConfig = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: (data: PlayoutConfigUpdate) =>
      request<PlayoutConfig>(bridgeUrl, "/config", {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      invalidate();
      toast.success("Playout settings saved");
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

export interface PlayoutSource {
  source: number;
  name: string;
  shortName: string;
  internal: boolean;
}

export interface PlayoutSources {
  /** Every source; the program feed may be a plain SDI input */
  sources: PlayoutSource[];
  me_outputs: Array<{ source: number; name: string }>;
  offline: boolean;
}

export const usePlayoutSources = (bridgeUrl: Url, enabled: boolean) =>
  useQuery({
    queryKey: [KEY, bridgeUrl, "sources"],
    queryFn: () => request<PlayoutSources>(bridgeUrl, "/sources"),
    enabled: !!bridgeUrl && enabled,
    retry: false,
  });

export interface VmixInput {
  number: number;
  key: string;
  title: string;
  type: string;
  state: string;
  position: number;
  duration: number;
}

export interface VmixStateResponse {
  active: number;
  preview: number;
  inputs: VmixInput[];
}

/** vMix inputs, so a cut-in can be picked by name rather than by number. */
export const useVmixInputs = (bridgeUrl: Url, enabled: boolean) =>
  useQuery({
    queryKey: [KEY, bridgeUrl, "vmix-inputs"],
    queryFn: () => request<VmixStateResponse>(bridgeUrl, "/vmix/inputs"),
    enabled: !!bridgeUrl && enabled,
    retry: false,
  });

// ============ Assets ============

export const usePlayoutAssets = (bridgeUrl: Url) =>
  useQuery({
    queryKey: [KEY, bridgeUrl, "assets"],
    queryFn: () => request<PlayoutAsset[]>(bridgeUrl, "/assets"),
    enabled: !!bridgeUrl,
  });

export const useCreatePlayoutAsset = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: (data: PlayoutAssetInsert) =>
      request<PlayoutAsset>(bridgeUrl, "/assets", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      invalidate();
      toast.success("Artwork added");
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useUpdatePlayoutAsset = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<PlayoutAssetInsert>;
    }) =>
      request<PlayoutAsset>(bridgeUrl, `/assets/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    onSuccess: () => invalidate(),
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useDeletePlayoutAsset = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: (id: string) =>
      request<void>(bridgeUrl, `/assets/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      invalidate();
      toast.success("Artwork deleted");
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useSyncPlayoutAsset = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: (id: string) =>
      request<MediaSyncResult>(bridgeUrl, `/assets/${id}/sync`, {
        method: "POST",
      }),
    onSuccess: (result) => {
      invalidate();
      toast.success(
        result.uploaded
          ? `Uploaded to media pool slot ${result.media_slot}`
          : `Already in slot ${result.media_slot}`,
      );
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useSyncAllPlayoutAssets = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: () =>
      request<MediaSyncResult[]>(bridgeUrl, "/assets/sync-all", {
        method: "POST",
      }),
    onSuccess: (results) => {
      invalidate();
      const uploaded = results.filter((r) => r.uploaded).length;
      toast.success(`Synced ${results.length} asset(s), ${uploaded} uploaded`);
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

// ============ Playouts ============

export const usePlayouts = (bridgeUrl: Url) =>
  useQuery({
    queryKey: [KEY, bridgeUrl, "playouts"],
    queryFn: () => request<PlayoutWithAsset[]>(bridgeUrl, "/playouts"),
    enabled: !!bridgeUrl,
  });

export const useCreatePlayout = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: (data: PlayoutInsert) =>
      request<Playout>(bridgeUrl, "/playouts", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      invalidate();
      toast.success("Playout created");
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useUpdatePlayout = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PlayoutUpdate }) =>
      request<Playout>(bridgeUrl, `/playouts/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      invalidate();
      toast.success("Playout saved");
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useDeletePlayout = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: (id: string) =>
      request<void>(bridgeUrl, `/playouts/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      invalidate();
      toast.success("Playout deleted");
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

export const usePreviewPlayout = (bridgeUrl: Url) =>
  useMutation({
    mutationFn: ({ id, holdSeconds }: { id: string; holdSeconds?: number }) =>
      request<{ ok: boolean }>(bridgeUrl, `/playouts/${id}/preview`, {
        method: "POST",
        // Omitted, not defaulted: the asset's own length should apply.
        body: JSON.stringify(
          holdSeconds != null ? { hold_seconds: holdSeconds } : {},
        ),
      }),
    onSuccess: () => toast.success("Preview taken to air"),
    onError: (error: Error) => toast.error(error.message),
  });

// ============ Playlists ============

export const usePlaylists = (bridgeUrl: Url) =>
  useQuery({
    queryKey: [KEY, bridgeUrl, "playlists"],
    queryFn: () => request<PlaylistWithItems[]>(bridgeUrl, "/playlists"),
    enabled: !!bridgeUrl,
  });

export const useCreatePlaylist = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      request<Playlist>(bridgeUrl, "/playlists", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      invalidate();
      toast.success("Playlist created");
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useUpdatePlaylist = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: ({
      playlistId,
      data,
    }: {
      playlistId: string;
      data: PlaylistUpdate;
    }) =>
      request<Playlist>(bridgeUrl, `/playlists/${playlistId}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    onSuccess: () => invalidate(),
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useDeletePlaylist = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: (id: string) =>
      request<void>(bridgeUrl, `/playlists/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      invalidate();
      toast.success("Playlist deleted");
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useCreatePlaylistItem = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: ({
      playlistId,
      data,
    }: {
      playlistId: string;
      data: PlaylistItemInsert;
    }) =>
      request<PlaylistItem>(bridgeUrl, `/playlists/${playlistId}/items`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      invalidate();
      toast.success("Item added");
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useUpdatePlaylistItem = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: ({
      itemId,
      data,
    }: {
      itemId: string;
      data: Partial<PlaylistItemInsert>;
    }) =>
      request<PlaylistItem>(bridgeUrl, `/items/${itemId}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    onSuccess: () => invalidate(),
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useDeletePlaylistItem = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: (itemId: string) =>
      request<void>(bridgeUrl, `/items/${itemId}`, { method: "DELETE" }),
    onSuccess: () => invalidate(),
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useCreateTrigger = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: ({
      playlistId,
      data,
    }: {
      playlistId: string;
      data: PlaylistTriggerInsert;
    }) =>
      request<PlaylistTrigger>(bridgeUrl, `/playlists/${playlistId}/triggers`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      invalidate();
      toast.success("Start time added");
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useUpdateTrigger = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: ({
      triggerId,
      data,
    }: {
      triggerId: string;
      data: Partial<PlaylistTriggerInsert>;
    }) =>
      request<PlaylistTrigger>(bridgeUrl, `/triggers/${triggerId}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    onSuccess: () => invalidate(),
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useDeleteTrigger = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: (triggerId: string) =>
      request<void>(bridgeUrl, `/triggers/${triggerId}`, { method: "DELETE" }),
    onSuccess: () => invalidate(),
    onError: (error: Error) => toast.error(error.message),
  });
};

// ============ Runs ============

export const usePlayoutReadiness = (bridgeUrl: Url, playlistId?: string) =>
  useQuery({
    queryKey: [KEY, bridgeUrl, "readiness", playlistId],
    queryFn: () =>
      request<PlayoutReadiness>(
        bridgeUrl,
        `/readiness${playlistId ? `?playlistId=${playlistId}` : ""}`,
      ),
    enabled: !!bridgeUrl,
  });

/** Polled: the scheduler runs on the bridge, so the UI has to ask. */
export const useActiveRuns = (bridgeUrl: Url) =>
  useQuery({
    queryKey: [KEY, bridgeUrl, "active-runs"],
    queryFn: () => request<ActiveRunState[]>(bridgeUrl, "/runs/active"),
    enabled: !!bridgeUrl,
    refetchInterval: 1000,
  });

export const useStartPlaylist = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: ({
      playlistId,
      force,
    }: {
      playlistId: string;
      force?: boolean;
    }) =>
      request<PlaylistRun>(bridgeUrl, `/playlists/${playlistId}/start`, {
        method: "POST",
        body: JSON.stringify({ force: force ?? false }),
      }),
    onSuccess: () => {
      invalidate();
      toast.success("Run started");
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useStopRun = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: (runId: string) =>
      request<{ ok: boolean }>(bridgeUrl, `/runs/${runId}/stop`, {
        method: "POST",
      }),
    onSuccess: () => {
      invalidate();
      toast.success("Run stopped");
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

export const usePauseRun = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: ({ runId, paused }: { runId: string; paused: boolean }) =>
      request<PlaylistRun>(
        bridgeUrl,
        `/runs/${runId}/${paused ? "pause" : "resume"}`,
        { method: "POST" },
      ),
    onSuccess: (_run, { paused }) => {
      invalidate();
      toast.success(paused ? "Run paused" : "Run resumed");
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useSkipNext = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: (runId: string) =>
      request<{ skipped_item_id: string | null }>(
        bridgeUrl,
        `/runs/${runId}/skip-next`,
        { method: "POST" },
      ),
    onSuccess: (result) => {
      invalidate();
      toast.success(
        result.skipped_item_id ? "Item skipped" : "Nothing to skip",
      );
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useRunLog = (bridgeUrl: Url, limit = 50) =>
  useQuery({
    queryKey: [KEY, bridgeUrl, "runs", limit],
    queryFn: () => request<RunWithLog[]>(bridgeUrl, `/runs?limit=${limit}`),
    enabled: !!bridgeUrl,
  });

// ============ Counters ============

export const usePlayoutCounters = (bridgeUrl: Url) =>
  useQuery({
    queryKey: [KEY, bridgeUrl, "counters"],
    queryFn: () => request<PlayoutCounters>(bridgeUrl, "/counters"),
    enabled: !!bridgeUrl,
  });

export const useResetPlayoutCounters = (bridgeUrl: Url) => {
  const invalidate = useInvalidate(bridgeUrl);
  return useMutation({
    mutationFn: () =>
      request<PlayoutConfig>(bridgeUrl, "/counters/reset", { method: "POST" }),
    onSuccess: () => {
      invalidate();
      toast.success("Counters reset");
    },
    onError: (error: Error) => toast.error(error.message),
  });
};
